import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Archive,
  CircleCheck,
  Eye,
  FileWarning,
  Hourglass,
  Landmark,
  MapPin,
  Package,
  Pencil,
  SearchX,
  TrendingDown,
  UserCheck,
  Users,
  Wrench,
  ArrowRight,
} from "lucide-react";
import api from "../../../services/apiService";
import DataTable, { createActionsColumn } from "../../../components/data-table";
import KpiCard from "../../../components/dashboard/KpiCard";
import NoticeBoardCard from "../../../components/notices/NoticeBoardCard";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 });
const fmtN = (n) => Number(n || 0).toLocaleString();

// ── Reusable pieces ──────────────────────────────────────────────────────────

const share = (part, whole) =>
  whole ? `${((Number(part || 0) / whole) * 100).toFixed(1)}%` : "0.0%";

// Display config per breakdown. Labels/colors map API keys; every number is
// derived from the /hr/dashboard `employees` payload at render time.
const EMPLOYEE_BREAKDOWNS = [
  {
    title: "Employee Status",
    source: "byStatus",
    segments: {
      active: {
        label: "Active Regular",
        bar: "bg-emerald-500",
        dot: "bg-emerald-500",
      },
      probation: {
        label: "Probation Period",
        bar: "bg-amber-400",
        dot: "bg-amber-400",
      },
      resigned: {
        label: "Resigned (Notice)",
        bar: "bg-slate-400",
        dot: "bg-slate-400",
      },
      terminated: {
        label: "Terminated",
        bar: "bg-rose-500",
        dot: "bg-rose-500",
      },
    },
    // Still on the books and not leaving.
    insight: (v, total) =>
      `Retention healthy: ${share(v.active + v.probation, total)}`,
    action: { label: "View logs", to: "/hr/employees" },
  },
  {
    title: "Employment Type",
    source: "byType",
    segments: {
      full_time: {
        label: "Full Time",
        bar: "bg-[#2470B7] dark:bg-blue-500",
        dot: "bg-[#2470B7] dark:bg-blue-500",
      },
      contractor: {
        label: "Contractor / Advisory",
        bar: "bg-violet-500",
        dot: "bg-violet-500",
      },
      intern: {
        label: "Intern Fellowship",
        bar: "bg-indigo-400",
        dot: "bg-indigo-400",
      },
      part_time: { label: "Part Time", bar: "bg-sky-400", dot: "bg-sky-400" },
    },
    insight: (v, total) =>
      `Direct hires: ${share(v.full_time + v.part_time, total)}`,
    action: { label: "Manage contracts", to: "/hr/employees" },
  },
  {
    title: "Work Mode",
    source: "byMode",
    segments: {
      onsite: {
        label: "Onsite Primary",
        bar: "bg-teal-500",
        dot: "bg-teal-500",
      },
      hybrid: {
        label: "Hybrid (3+2 Model)",
        bar: "bg-sky-500",
        dot: "bg-sky-500",
      },
      remote: { label: "Fully Remote", bar: "bg-cyan-300", dot: "bg-cyan-300" },
    },
    ratio: true,
    insight: (v, total) =>
      `Flexible workforce: ${share(v.hybrid + v.remote, total)}`,
    action: { label: "Office density", to: "/hr/locations" },
  },
];

function BreakdownCard({ config, data, onNavigate }) {
  const values = Object.fromEntries(
    Object.keys(config.segments).map((k) => [k, Number(data?.[k] || 0)]),
  );
  const total = Object.values(values).reduce((sum, v) => sum + v, 0);
  // Largest segment first, matching how the breakdown reads top-down.
  const rows = Object.keys(config.segments)
    .map((key) => ({ key, value: values[key], ...config.segments[key] }))
    .sort((x, y) => y.value - x.value);

  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:border-slate-700 dark:bg-slate-800 dark:shadow-none">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {config.title}
        </h3>
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          Total: <span className="tabular-nums">{fmtN(total)}</span>
        </span>
      </div>

      <div className="mt-5 space-y-4">
        {rows.map(({ key, label, value, bar, dot }) => (
          <div key={key}>
            <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
              <span className="flex min-w-0 items-center gap-2 text-slate-600 dark:text-slate-300">
                <StatusDot color={dot} />
                <span className="truncate">{label}</span>
              </span>
              <span className="shrink-0 tabular-nums">
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {fmtN(value)}
                </span>
                <span className="ml-1 text-slate-400">
                  ({share(value, total)})
                </span>
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
              <div
                className={`h-full rounded-full ${bar} transition-[width] duration-500`}
                style={{ width: total ? `${(value / total) * 100}%` : "0%" }}
              />
            </div>
          </div>
        ))}

        {config.ratio && (
          <div className="rounded-xl">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Ratio Distribution
              </span>
              <span className="flex items-center gap-1.5 text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100">
                {Object.keys(config.segments).map((k, i) => (
                  <React.Fragment key={k}>
                    {i > 0 && (
                      <span className="font-normal text-slate-300 dark:text-slate-600">
                        :
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <StatusDot color={config.segments[k].dot} />
                      {fmtN(values[k])}
                    </span>
                  </React.Fragment>
                ))}
              </span>
            </div>
            <div className="mt-2.5 flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              {Object.keys(config.segments).map((k) =>
                values[k] && total ? (
                  <div
                    key={k}
                    className={config.segments[k].bar}
                    style={{ width: `${(values[k] / total) * 100}%` }}
                    title={`${config.segments[k].label}: ${fmtN(values[k])}`}
                  />
                ) : null,
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4 text-xs dark:border-slate-700">
        <span className="text-slate-500 dark:text-slate-400">{config.insight(values, total)}</span>

        <button
          type="button"
          onClick={() => onNavigate(config.action.to)}
          className="group inline-flex items-center gap-1.5 rounded-xl bg-indigo-50 px-4 py-2 text-xs font-semibold text-indigo-700 transition-all duration-200 hover:bg-indigo-100 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 active:scale-[0.98] dark:bg-indigo-500/15 dark:text-indigo-300 dark:hover:bg-indigo-500/25 dark:hover:text-indigo-200 dark:focus:ring-indigo-400/40"
        >
          <span>{config.action.label}</span>
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
}

function StatusDot({ color }) {
  return <span className={`inline-block h-2 w-2 rounded-full ${color}`} />;
}

const FLEET_STATUSES = {
  available: {
    label: "Available",
    desc: "Ready to deploy",
    icon: CircleCheck,
    value: "text-emerald-600 dark:text-emerald-400",
    dot: "bg-emerald-500",
    iconColor: "text-emerald-500 dark:text-emerald-400",
    bar: "bg-emerald-500",
  },
  assigned: {
    label: "Assigned",
    desc: "Active users",
    icon: UserCheck,
    value: "text-indigo-600 dark:text-indigo-400",
    dot: "bg-indigo-500",
    iconColor: "text-indigo-500 dark:text-indigo-400",
    bar: "bg-indigo-500",
  },
  repair: {
    label: "In Repair",
    desc: "IT Service Bay",
    icon: Wrench,
    value: "text-orange-500 dark:text-orange-400",
    dot: "bg-orange-500",
    iconColor: "text-orange-500 dark:text-orange-400",
    bar: "bg-orange-400",
  },
  lost: {
    label: "Lost",
    desc: "Unrecoverable / missing",
    icon: SearchX,
    value: "text-rose-600 dark:text-rose-400",
    dot: "bg-rose-500",
    iconColor: "text-rose-500 dark:text-rose-400",
    bar: "bg-rose-500",
  },
  retired: {
    label: "Retired",
    desc: "Depreciated / decommissioned",
    icon: Archive,
    value: "text-slate-500 dark:text-slate-300",
    dot: "bg-slate-400",
    iconColor: "text-slate-400",
    bar: "bg-slate-400",
  },
};

const FLEET_CARD_ORDER = ["available", "assigned", "repair", "lost", "retired"];
const FLEET_BAR_ORDER = ["assigned", "available", "repair", "retired", "lost"];

function AssetFleetCard({ assets, onViewAssets }) {
  const byStatus = assets.byStatus || {};
  const total =
    Number(assets.total) ||
    FLEET_CARD_ORDER.reduce((s, k) => s + Number(byStatus[k] || 0), 0);
  const efficiency = total
    ? ((Number(byStatus.assigned || 0) / total) * 100).toFixed(1)
    : "0.0";

  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200/70 bg-[#FAFBFD] p-5 shadow-[0_1px_3px_rgba(15,23,42,0.06)] sm:p-6 dark:border-slate-700 dark:bg-slate-800 dark:shadow-none">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Asset Fleet &amp; Inventory
            </h3>
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              {fmtN(total)} total
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Real-time status of company workstations, laptops &amp; mobile
            equipment
          </p>
        </div>
        <button
          type="button"
          onClick={onViewAssets}
          className="group inline-flex items-center gap-1.5 rounded-xl bg-indigo-50 px-4 py-2 text-xs font-semibold text-indigo-700 transition-all duration-200 hover:bg-indigo-100 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 active:scale-[0.98] dark:bg-indigo-500/15 dark:text-indigo-300 dark:hover:bg-indigo-500/25 dark:hover:text-indigo-200 dark:focus:ring-indigo-400/40"
        >
          <span>View Asset</span>
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        {FLEET_CARD_ORDER.map((key) => {
          const s = FLEET_STATUSES[key];
          const Icon = s.icon;
          return (
            <div
              key={key}
              className="flex flex-col rounded-xl border border-slate-200/80 bg-white p-3.5 dark:border-slate-700 dark:bg-slate-900/50"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {s.label}
                  </span>
                </div>
                <Icon
                  size={16}
                  strokeWidth={1.75}
                  className={`shrink-0 ${s.iconColor}`}
                />
              </div>
              <p
                className={`mt-2 text-2xl font-bold leading-tight tabular-nums ${s.value}`}
              >
                {fmtN(byStatus[key])}
              </p>
              <p className="mt-1 text-[11px] leading-snug text-slate-400">
                {s.desc}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-auto pt-6">
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            Overall Fleet Deployed Efficiency
          </span>
          <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
            {efficiency}%
          </span>
        </div>
        <div className="flex h-2.5 w-full gap-0.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
          {FLEET_BAR_ORDER.map((key) => {
            const value = Number(byStatus[key] || 0);
            if (!value || !total) return null;
            return (
              <div
                key={key}
                className={FLEET_STATUSES[key].bar}
                style={{ width: `${(value / total) * 100}%` }}
              />
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
          {FLEET_BAR_ORDER.map((key) => (
            <div
              key={key}
              className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"
            >
              <StatusDot color={FLEET_STATUSES[key].dot} />
              <span className="font-semibold text-slate-800 dark:text-slate-200">
                {fmtN(byStatus[key])}
              </span>
              <span>{FLEET_STATUSES[key].label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const STATUS_BADGE = {
  active:
    "bg-green-50 border-green-200 text-green-700 dark:bg-green-500/10 dark:border-green-500/30 dark:text-green-400",
  probation:
    "bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-500/10 dark:border-yellow-500/30 dark:text-yellow-300",
  resigned:
    "bg-gray-100 border-gray-200 text-gray-500 dark:bg-slate-700/60 dark:border-slate-600 dark:text-slate-300",
  terminated:
    "bg-red-50 border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-400",
};

const PAYROLL_STATUS_PILL = {
  draft: {
    text: "Draft",
    pill: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    dot: "bg-slate-400",
  },
  processed: {
    text: "Processed",
    pill: "bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
    dot: "bg-violet-500",
  },
  approved: {
    text: "Approved",
    pill: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300",
    dot: "bg-indigo-500",
  },
  paid: {
    text: "Disbursement Complete",
    pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    dot: "bg-emerald-500",
  },
  cancelled: {
    text: "Cancelled",
    pill: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
    dot: "bg-rose-500",
  },
};

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// "Sep 01 – Sep 30, 2026" from a 1-based month + year; null when either is missing.
const payrollPeriod = (month, year) => {
  const m = Number(month);
  const y = Number(year);
  if (!m || !y || m < 1 || m > 12) return null;
  const last = new Date(y, m, 0).getDate();
  const mon = MONTHS_SHORT[m - 1];
  return `${mon} 01 – ${mon} ${String(last).padStart(2, "0")}, ${y}`;
};

const pct = (part, whole) =>
  whole ? `${((Number(part || 0) / whole) * 100).toFixed(2)}%` : "0.00%";

function PayrollSummaryCard({ payroll, onViewDetails, onCreate }) {
  if (!payroll) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-slate-200/70 bg-white py-10 text-slate-400 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:border-slate-700 dark:bg-slate-800 dark:shadow-none">
        <p className="text-sm">No payroll processed yet.</p>
        <button
          onClick={onCreate}
          className="mt-3 rounded-lg bg-[#2470B7] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 active:scale-[0.98] dark:bg-blue-600 dark:hover:bg-blue-500 dark:focus-visible:ring-sky-400/40"
        >
          Create Payroll
        </button>
      </div>
    );
  }

  const gross = Number(payroll.totalGross || 0);
  const status = PAYROLL_STATUS_PILL[payroll.status] || {
    text: payroll.status,
    pill: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    dot: "bg-slate-400",
  };
  const isPaid = payroll.status === "paid";
  const period = payrollPeriod(payroll.month, payroll.year);
  const allocated = pct(
    Number(payroll.totalNet || 0) + Number(payroll.totalDeductions || 0),
    gross,
  ).replace(".00%", "%");

  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.06)] sm:p-6 dark:border-slate-700 dark:bg-slate-800 dark:shadow-none">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {payroll.label} Payroll
            </h3>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${status.pill}`}
            >
              <StatusDot color={status.dot} />
              {status.text}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {fmtN(payroll.employeeCount)} eligible workforce payouts processed
            {period && (
              <>
                <span className="mx-2 text-slate-300 dark:text-slate-600">
                  •
                </span>
                {period}
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onViewDetails}
          className="group inline-flex items-center gap-1.5 rounded-xl bg-indigo-50 px-4 py-2 text-xs font-semibold text-indigo-700 transition-all duration-200 hover:bg-indigo-100 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 active:scale-[0.98] dark:bg-indigo-500/15 dark:text-indigo-300 dark:hover:bg-indigo-500/25 dark:hover:text-indigo-200 dark:focus:ring-indigo-400/40"
        >
          View Run Details
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </div>

      {/* Gross */}
      <div className="mt-5 rounded-xl border border-slate-200/80 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/50">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <Landmark
              size={15}
              strokeWidth={1.75}
              className="shrink-0 text-slate-400"
            />
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Gross Payroll
            </p>
          </div>
          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {allocated} of Allocation
          </span>
        </div>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-x-6 gap-y-1">
          <p className="text-2xl font-semibold tracking-tight tabular-nums text-slate-900 dark:text-slate-100">
            {fmt(payroll.totalGross)}
          </p>
          <p className="pb-1 text-xs text-slate-400">
            Base salary &amp; executive performance bonus
          </p>
        </div>
      </div>

      {/* Deductions + Net */}
      <div className="mt-3 grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col rounded-xl border border-rose-100 bg-rose-50/40 p-4 dark:border-rose-500/25 dark:bg-rose-500/[0.08]">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300">
              Deductions
            </p>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400">
              <TrendingDown size={15} strokeWidth={2} />
            </span>
          </div>
          <p className="text-2xl font-semibold tracking-tight tabular-nums text-rose-600 dark:text-rose-400">
            {fmt(payroll.totalDeductions)}
          </p>
        </div>

        <div className="flex flex-col rounded-xl border border-emerald-100 bg-emerald-50/40 p-4 dark:border-emerald-500/25 dark:bg-emerald-500/[0.08]">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              Net Disbursed
            </p>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
              <CircleCheck size={15} strokeWidth={2} />
            </span>
          </div>
          <p className="text-2xl font-semibold tracking-tight tabular-nums text-emerald-600 dark:text-emerald-400">
            {fmt(payroll.totalNet)}
          </p>
        </div>
      </div>
    </div>
  );
}

const recentEmployeeColumns = (navigate) => [
  {
    key: "employeeCode",
    label: "Code",
    sortable: true,
    searchable: true,
    render: (v) => (
      <span className="font-mono text-xs text-gray-400 dark:text-slate-400">
        {v || "—"}
      </span>
    ),
  },
  {
    key: "fullName",
    label: "Name",
    sortable: true,
    searchable: true,
    render: (v) => (
      <span className="font-medium text-gray-900 dark:text-slate-100">{v}</span>
    ),
  },
  {
    key: "employmentType",
    label: "Type",
    sortable: true,
    render: (v) => (
      <span className="capitalize">{v?.replace("_", " ") || "—"}</span>
    ),
  },
  {
    key: "status",
    label: "Status",
    sortable: true,
    render: (v) => (
      <span
        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[v] || ""}`}
      >
        {v}
      </span>
    ),
  },
  {
    key: "createdAt",
    label: "Joined",
    sortable: true,
    render: (v) =>
      new Date(v).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      }),
  },
  createActionsColumn(
    (emp) => [
      {
        label: "View details",
        icon: Eye,
        onSelect: () => navigate(`/hr/employees/${emp.id}`),
      },
      {
        label: "Edit",
        icon: Pencil,
        onSelect: () => navigate(`/hr/employees/${emp.id}/edit`),
      },
    ],
    { getRowLabel: (emp) => emp.fullName },
  ),
];

// ── Main component ───────────────────────────────────────────────────────────

function HrDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/hr/dashboard", { headers: authHeaders() })
      .then((r) => setStats(r.data))
      .catch(() => setError("Failed to load dashboard data."))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading)
    return (
      <div className="flex justify-center py-20 text-gray-400 dark:text-slate-400">
        Loading dashboard…
      </div>
    );

  if (error)
    return (
      <div className="flex justify-center py-20 text-red-500 dark:text-red-400">
        {error}
      </div>
    );

  const { employees, assets, locations, documents, payroll } = stats;

  return (
    <div className="space-y-6">
      <p className="text-sm text-app-muted">
        Overview of your workforce, assets and payroll.
      </p>

      {/* ── Top KPI row ── */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard
          label="Total Employees"
          value={fmtN(employees.total)}
          icon={Users}
          sub={`${fmtN(employees.byStatus.active)} active`}
          color="blue"
          onClick={() => navigate("/hr/employees")}
        />
        <KpiCard
          label="On Probation"
          value={fmtN(employees.byStatus.probation)}
          icon={Hourglass}
          badge={employees.byStatus.probation > 0 ? "Reviewing" : undefined}
          color="orange"
          onClick={() => navigate("/hr/employees")}
        />
        <KpiCard
          label="Total Assets"
          value={fmtN(assets.total)}
          icon={Package}
          sub={`${fmtN(assets.byStatus.available)} available`}
          color="dark"
          onClick={() => navigate("/hr/assets")}
        />
        <KpiCard
          label="Locations"
          value={fmtN(locations.active)}
          icon={MapPin}
          sub={`${fmtN(locations.total)} total`}
          color="green"
          onClick={() => navigate("/hr/locations")}
        />
        <KpiCard
          label="Docs Expiring"
          value={fmtN(documents.expiring)}
          icon={FileWarning}
          badge={documents.expiring > 0 ? "Action Req." : undefined}
          color="red"
          onClick={() => navigate("/hr/employees")}
        />
      </div>

      <NoticeBoardCard />

      {/* ── Employee breakdowns ── */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {EMPLOYEE_BREAKDOWNS.map((config) => (
          <BreakdownCard
            key={config.title}
            config={config}
            data={employees[config.source]}
            onNavigate={navigate}
          />
        ))}
      </div>

      {/* ── Asset status + Payroll summary ── */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="xl:col-span-7">
          <AssetFleetCard
            assets={assets}
            onViewAssets={() => navigate("/hr/assets")}
          />
        </div>
        <div className="xl:col-span-5">
          <PayrollSummaryCard
            payroll={payroll}
            onViewDetails={() =>
              navigate(
                payroll?.id ? `/hr/payroll/${payroll.id}` : "/hr/payroll",
              )
            }
            onCreate={() => navigate("/hr/payroll")}
          />
        </div>
      </div>

      {/* ── Recent employees ── */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
          Recently Added Employees
        </h3>
        <DataTable
          ariaLabel="Recently added employees"
          columns={recentEmployeeColumns(navigate)}
          data={employees.recent}
          searchable
          searchPlaceholder="Search code or name"
          pagination
          defaultPageSize={5}
          pageSizeOptions={[5, 10, 20]}
          onRowClick={(emp) => navigate(`/hr/employees/${emp.id}`)}
          labels={{ empty: "No employees added yet." }}
        />
      </section>
    </div>
  );
}

export default HrDashboard;
